// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
// * Icons
import {
  HomeWorkRounded as HomeWorkRoundedIcon,
  MonitorRounded as MonitorRoundedIcon,
} from '@mui/icons-material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';

// material ui
import { useTheme } from '@mui/material/styles';

// api
import {
  useGetEnergyGroupQuery,
  useGetEnergyGroupsQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

const ESGIcon = () => {
  const theme = useTheme();
  return (
    <img
      width="23"
      height="23"
      src="https://img.icons8.com/deco-glyph/48/hand-planting.png"
      alt="hand-planting"
      style={{
        filter: theme.palette.mode === 'dark' ? 'invert(100%)' : 'invert(40%)',
      }}
    />
  );
};

const createBaseItems = (t, navigate, energyGroupId, analysisType) => {
  let items = [
    {
      id: 'sites',
      label: t('app_bar.sites_overview'),
      icon: <HomeWorkRoundedIcon />,
      path: 'portfolio',
      conditions: ['portfolio'],
    },
    {
      id: 'benchmark',
      label: t('app_bar.benchmark'),
      icon: <InsightsRoundedIcon />,
      path: 'portfolio/building',
      conditions: ['portfolio'],
    },
    {
      id: 'buildings',
      label: t('app_bar.buildings_overview'),
      icon: <ApartmentRoundedIcon />,
      path: 'portfolio/matrikel',
      conditions: ['portfolio'],
    },
    {
      id: 'portfolio',
      label: t('app_bar.portfolio_analysis'),
      icon: <TableChartRoundedIcon />,
      path: `portfolio/${analysisType}/analysis/overview`,
      matchingPaths: [
        `portfolio/${analysisType}/analysis`,
        `portfolio/${analysisType}/pivot`,
      ],
      conditions: [
        'powerPortfolio',
        'heatPortfolio',
        'waterPortfolio',
        'powerPortfolioWithTree',
        'heatPortfolioWithTree',
        'waterPortfolioWithTree',
      ],
    },
    {
      id: 'consumption',
      label: t('app_bar.consumption_overview'),
      icon: <MonitorRoundedIcon />,
      path: `deepdive/${energyGroupId}/consumption-overview`,
      conditions: ['power', 'heat', 'water'],
    },
    {
      id: 'breakdown',
      label: t('app_bar.energy_breakdown'),
      icon: <LanRoundedIcon />,
      path: `deepdive/${energyGroupId}/energy-breakdown`,
      conditions: ['power', 'heat', 'water'],
      submenu: [
        {
          id: 'tree',
          label: t('app_bar.energy_tree'),
          path: `deepdive/${energyGroupId}/energy-breakdown`,
        },
        {
          id: 'tree-overview',
          label: t('app_bar.energy_tree_overview'),
          path: `deepdive/${energyGroupId}/energy-breakdown-table`,
        },
      ],
    },
    {
      id: 'portfolio-breakdown',
      label: t('app_bar.energy_breakdown'),
      icon: <LanRoundedIcon />,
      path: `portfolio/${analysisType}/${energyGroupId}/energy-breakdown`,
      conditions: [
        'powerPortfolio',
        'heatPortfolio',
        'waterPortfolio',
        'waterPortfolioWithTree',
        'heatPortfolioWithTree',
        'powerPortfolioWithTree',
      ],
      submenu: [
        {
          id: 'tree',
          label: t('app_bar.energy_tree'),
          path: `portfolio/${analysisType}/${energyGroupId}/energy-breakdown`,
          conditions: [
            'powerPortfolio',
            'heatPortfolio',
            'waterPortfolio',
            'waterPortfolioWithTree',
            'heatPortfolioWithTree',
            'powerPortfolioWithTree',
          ],
        },
        {
          id: 'tree-overview',
          label: t('app_bar.energy_tree_overview'),
          path: `portfolio/${analysisType}/${energyGroupId}/energy-breakdown-table`,
          conditions: ['powerPortfolioWithTree', 'lessThan250Nodes'],
        },
      ],
    },
    {
      id: 'analysis',
      label: t('app_bar.energy_analysis'),
      icon: <QueryStatsRoundedIcon />,
      path: `deepdive/${energyGroupId}/child-analysis`,
      conditions: ['power', 'heat', 'water'],
      submenu: [
        {
          id: 'breakdown-analysis',
          label: t('app_bar.energy_analysis_breakdown'),
          path: `deepdive/${energyGroupId}/child-analysis`,
          conditions: ['power', 'heat', 'water'],
        },
        {
          id: 'hour-analysis',
          label: t('app_bar.energy_analysis_hour'),
          path: `deepdive/${energyGroupId}/hour-analysis`,
          conditions: ['power', 'heat', 'water'],
        },
        {
          id: 'duration-analysis',
          label: t('app_bar.energy_analysis_duration'),
          path: `deepdive/${energyGroupId}/duration-analysis`,
          conditions: ['power'],
        },
        {
          id: 'node-analysis',
          label: t('app_bar.energy_analysis_node'),
          path: `deepdive/${energyGroupId}/energy-analysis`,
          conditions: ['power', 'heat', 'water'],
        },
        {
          id: 'compare-analysis',
          label: t('app_bar.energy_analysis_compare'),
          path: `deepdive/${energyGroupId}/comparison-analysis`,
          conditions: ['power', 'heat', 'water'],
        },
        {
          id: 'load-profile',
          label: t('app_bar.energy_analysis_load_profile'),
          path: `deepdive/${energyGroupId}/load-profile-analysis`,
          conditions: ['power'],
        },
      ],
    },
    {
      id: 'portfolio-meter-analysis',
      label: t('app_bar.portfolio-meter-analysis'),
      icon: <QueryStatsRoundedIcon />,
      path: `portfolio/${analysisType}/${energyGroupId}/child-analysis`,
      conditions: [
        'powerPortfolio',
        'heatPortfolio',
        'waterPortfolio',
        'powerPortfolioWithTree',
        'heatPortfolioWithTree',
        'waterPortfolioWithTree',
      ],
      submenu: [
        {
          id: 'breakdown-analysis',
          label: t('app_bar.energy_analysis_breakdown'),
          path: `portfolio/${analysisType}/${energyGroupId}/child-analysis`,
        },
        {
          id: 'hour-analysis',
          label: t('app_bar.energy_analysis_hour'),
          path: `portfolio/${analysisType}/${energyGroupId}/hour-analysis`,
        },
        {
          id: 'duration-analysis',
          label: t('app_bar.energy_analysis_duration'),
          path: `portfolio/${analysisType}/${energyGroupId}/duration-analysis`,
          conditions: ['powerPortfolioWithTree'],
        },
        {
          id: 'node-analysis',
          label: t('app_bar.energy_analysis_node'),
          path: `portfolio/${analysisType}/${energyGroupId}/energy-analysis`,
        },
        {
          id: 'compare-analysis',
          label: t('app_bar.energy_analysis_compare'),
          path: `portfolio/${analysisType}/${energyGroupId}/comparison-analysis`,
        },
        {
          id: 'load-profile',
          label: t('app_bar.energy_analysis_load_profile'),
          path: `portfolio/${analysisType}/${energyGroupId}/load-profile-analysis`,
        },
      ],
    },
    {
      id: 'initiatives',
      label: t('app_bar.initiatives'),
      icon: <EnergySavingsLeafRoundedIcon />,
      path: `deepdive/${energyGroupId}/energy-initiatives`,
      conditions: ['power', 'lessThan250Nodes'],
    },
    {
      id: 'esg',
      label: t('app_bar.esg'),
      icon: <ESGIcon />,
      path: `deepdive/${energyGroupId}/esg`,
      conditions: ['power', 'lessThan250Nodes'],
    },
    {
      id: 'portfolio-esg',
      label: t('app_bar.esg'),
      icon: <ESGIcon />,
      path: `portfolio/${analysisType}/${energyGroupId}/esg`,
      conditions: [
        'powerPortfolio',
        'powerPortfolioWithTree',
        'heatPortfolio',
        'heatPortfolioWithTree',
        'waterPortfolio',
        'waterPortfolioWithTree',
        'lessThan250Nodes',
      ],
    },
  ];

  return items;
};

const getViewType = (
  energyGroupIdFromUrl,
  selectedEnergyGroup,
  isPortfolio,
  isTypedPortfolio,
  analysisType,
  hasTypePortfolio,
) => {
  if (isPortfolio) return 'portfolio';
  if (isTypedPortfolio && hasTypePortfolio) {
    if (analysisType === 'power') return 'powerPortfolioWithTree';
    if (analysisType === 'heat') return 'heatPortfolioWithTree';
    if (analysisType === 'water') return 'waterPortfolioWithTree';
  }
  if (isTypedPortfolio) {
    if (analysisType === 'power') return 'powerPortfolio';
    if (analysisType === 'heat') return 'heatPortfolio';
    if (analysisType === 'water') return 'waterPortfolio';
  }
  if (!energyGroupIdFromUrl) return 'none';
  if (selectedEnergyGroup?.type === 'Power') return 'power';
  if (selectedEnergyGroup?.type === 'Water') return 'water';

  if (selectedEnergyGroup?.type === 'Heat') return 'heat';
  return 'none';
};

export { createBaseItems, getViewType };

const useEnergyAnalysisMenuItems = () => {
  const { t } = useTranslation('energy-analysis', 'common');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    accountId,
    analysisType,
    energyGroupId: energyGroupIdFromUrl,
  } = useParams({
    strict: false,
  });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupIdFromUrl },
    { skip: !energyGroupIdFromUrl },
  );
  const { data: energyGroups } = useGetEnergyGroupsQuery(
    { accountIds: [accountId] },
    { skip: !accountId },
  );
  const energyGroupId = analysisType
    ? energyGroups?.find(
        (group) =>
          group.type ===
          `Portfolio${analysisType?.charAt(0).toUpperCase() + analysisType?.slice(1)}`,
      )?.energyGroupId
    : selectedEnergyGroup?.energyGroupId;

  // Determine view context
  const isTypedPortfolio = Boolean(analysisType);
  const isPortfolio =
    location.pathname.includes('portfolio') && !isTypedPortfolio;
  const typePortfolio =
    energyGroups &&
    energyGroups.find(
      (group) =>
        group.type ===
        `Portfolio${analysisType?.charAt(0).toUpperCase() + analysisType?.slice(1)}`,
    );
  const hasTypePortfolio = Boolean(typePortfolio);
  const viewType = getViewType(
    energyGroupIdFromUrl,
    selectedEnergyGroup,
    isPortfolio,
    isTypedPortfolio,
    analysisType,
    hasTypePortfolio,
  );
  // Build base path helper
  const buildPath = (path) => {
    const base = `/${accountId}/energyxplorer/`;
    const fullPath = `${base}${path}`;
    const search = location.searchStr;
    const searchParams = new URLSearchParams(search);
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const extraParams =
      from && to
        ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        : '';
    return `${fullPath}${extraParams}`;
  };

  // Filter and build items
  const appBarItems = createBaseItems(
    t,
    navigate,
    energyGroupId,
    analysisType,
    location.searchStr,
  )
    .map((item) => {
      // Check if item has both portfolio and portfolioWithTree conditions
      const hasPortfolioCondition = item.conditions?.some(
        (cond) =>
          cond === 'powerPortfolio' ||
          cond === 'heatPortfolio' ||
          cond === 'waterPortfolio',
      );
      const hasPortfolioWithTreeCondition = item.conditions?.some((cond) =>
        cond.includes('WithTree'),
      );
      const isPortfolioWithoutTree =
        viewType === 'powerPortfolio' ||
        viewType === 'heatPortfolio' ||
        viewType === 'waterPortfolio';

      // If item has both conditions, keep it visible but disable if not in tree mode
      const shouldBeDisabled =
        hasPortfolioCondition &&
        hasPortfolioWithTreeCondition &&
        isPortfolioWithoutTree &&
        item.id !== 'portfolio';

      // Only filter out items that don't match current viewType at all
      const shouldBeVisible =
        !item.conditions || item.conditions.includes(viewType);

      if (!shouldBeVisible && !shouldBeDisabled) return null;

      if (
        item.conditions?.includes('lessThan250Nodes') &&
        typePortfolio?.energyTreeNodes?.length > 250
      ) {
        return null;
      }

      return {
        ...item,
        disabled: shouldBeDisabled,
        onClick: () => {
          const targetPath = buildPath(item.path);
          navigate({ to: targetPath, replace: false });
        },
        submenu: item.submenu
          ?.filter(
            (sub) =>
              !sub.conditions ||
              (sub.conditions.includes(viewType) &&
                !(
                  sub.conditions.includes('lessThan250Nodes') &&
                  typePortfolio?.energyTreeNodes?.length > 250
                )),
          )
          .map((sub) => ({
            ...sub,
            onClick: () => {
              const targetPath = buildPath(sub.path);
              navigate({ to: targetPath, replace: false });
            },
          })),
      };
    })
    .filter(Boolean);

  return { appBarItems, viewType };
};

export default useEnergyAnalysisMenuItems;
